import {
  findElementCategoryAndSelectionsByProductId,
  getElementCategoryId,
  isProductCategory,
} from '@shared/helpers/element-category.helpers';
import type { ElementPath } from '@shared/helpers/recursive-element.helpers';
import { isElement } from '@shared/helpers/recursive-element.helpers';
import {
  ElementCategoryID,
  isProductCategoryId,
  type ProductCategoryId,
} from '@shared/models/element-categories.model';
import type { IProduct } from '@shared/models/product.model';
import type { IProductElement } from '@shared/models/project.model';

const isBoverketMaterialCategory = (
  categoryId: ElementCategoryID,
): categoryId is
  | ElementCategoryID.Concrete
  | ElementCategoryID.Metal
  | ElementCategoryID.Wood =>
  categoryId === ElementCategoryID.Concrete ||
  categoryId === ElementCategoryID.Metal ||
  categoryId === ElementCategoryID.Wood;

/**
 * Resolve betong/stål/trä for Boverket material shares from product catalog,
 * product picker category, or nearest parent element category.
 */
export const resolveBoverketMaterialCategoryId = (
  productElement: IProductElement,
  path: ElementPath,
  product?: IProduct,
): ElementCategoryID | undefined => {
  const categoryIds: ElementCategoryID[] = [];

  if (product?.category_id) {
    categoryIds.push(getElementCategoryId(product.category_id));
  }

  const fromProductPicker = findElementCategoryAndSelectionsByProductId(
    productElement.product_id,
    productElement.generic_product_id,
  );
  if (fromProductPicker) {
    categoryIds.push(fromProductPicker.category_id);
  }

  for (const categoryId of categoryIds) {
    if (isBoverketMaterialCategory(categoryId)) return categoryId;
  }

  for (let i = path.length - 1; i >= 0; i--) {
    const node = path[i];
    if (!isElement(node)) continue;
    const categoryId = getElementCategoryId(node);
    if (isBoverketMaterialCategory(categoryId)) return categoryId;
  }

  return undefined;
};

const resolveProductCategoryId = (
  productElement: IProductElement,
  path: ElementPath,
  product?: IProduct,
): ProductCategoryId => {
  if (product?.category_id) {
    const fromProduct = getElementCategoryId(product.category_id);
    if (isProductCategory(fromProduct)) return fromProduct;
  }

  const fromProductPicker = findElementCategoryAndSelectionsByProductId(
    productElement.product_id,
    productElement.generic_product_id,
  );
  if (fromProductPicker && isProductCategory(fromProductPicker.category_id)) {
    return fromProductPicker.category_id;
  }

  for (let i = path.length - 1; i >= 0; i--) {
    const node = path[i];
    if (!isElement(node)) continue;
    const categoryId = getElementCategoryId(node);
    if (isProductCategory(categoryId)) return categoryId;
  }

  return ElementCategoryID.OtherProduct;
};

// Swedish labels are an intentional exception: Boverket's XML import is a Swedish
// government e-service and expects material type names in Swedish (cf. example XML).
const BOVERKET_OTHER_MATERIAL_GROUP_LABELS_SV: Record<
  ProductCategoryId,
  string
> = {
  [ElementCategoryID.Concrete]: 'Betong',
  [ElementCategoryID.Wood]: 'Trä',
  [ElementCategoryID.Insulation]: 'Isolering',
  [ElementCategoryID.Metal]: 'Stål',
  [ElementCategoryID.Gypsum]: 'Gips',
  [ElementCategoryID.Ceramics]: 'Keramik',
  [ElementCategoryID.WindowsDoors]: 'Fönster och dörrar',
  [ElementCategoryID.Labour]: 'Arbete',
  [ElementCategoryID.OtherProduct]: 'Övrigt',
  [ElementCategoryID.Energy]: 'Energi',
};

/**
 * Simplified material label for Boverket övrigt 1/2 — same grouping as Products tab
 * "group by category", exported in Swedish for Boverket XML.
 */
export const resolveBoverketOtherMaterialGroupLabel = (
  productElement: IProductElement,
  path: ElementPath,
  product?: IProduct,
): string => {
  const categoryId = resolveProductCategoryId(productElement, path, product);
  if (!isProductCategoryId(categoryId)) return 'Övrigt';
  return BOVERKET_OTHER_MATERIAL_GROUP_LABELS_SV[categoryId];
};
